/**
 * Items Store Slice
 *
 * Zustand slice for purchasable item management state.
 * Handles CRUD operations for items belonging to an application.
 */

import { itemsApi, type CreateItemRequest, type UpdateItemRequest } from '@/api/items';
import type { PurchasableItem } from '@/types/purchase.types';

import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface ItemsSlice {
  items: PurchasableItem[];
  currentItem: PurchasableItem | null;
  itemsLoading: boolean;
  itemsError: string | null;

  fetchItems: (appId: string) => Promise<void>;
  fetchItemById: (appId: string, itemId: string) => Promise<void>;
  createItem: (appId: string, data: CreateItemRequest) => Promise<PurchasableItem>;
  updateItem: (appId: string, itemId: string, data: UpdateItemRequest) => Promise<PurchasableItem>;
  clearCurrentItem: () => void;
  clearItemsError: () => void;
}

// ============================================================================
// Slice Creator
// ============================================================================

export const createItemsSlice: StateCreator<ItemsSlice> = (set) => ({
  items: [],
  currentItem: null,
  itemsLoading: false,
  itemsError: null,

  fetchItems: async (appId: string) => {
    set({ itemsLoading: true, itemsError: null });
    try {
      const response = await itemsApi.listItems(appId);
      set({ items: response.items, itemsLoading: false });
    } catch (error) {
      set({
        itemsLoading: false,
        itemsError: error instanceof Error ? error.message : 'Failed to fetch items',
      });
    }
  },

  fetchItemById: async (appId: string, itemId: string) => {
    set({ itemsLoading: true, itemsError: null });
    try {
      const item = await itemsApi.getItem(appId, itemId);
      set({ currentItem: item, itemsLoading: false });
    } catch (error) {
      set({
        itemsLoading: false,
        itemsError: error instanceof Error ? error.message : 'Failed to fetch item',
      });
    }
  },

  createItem: async (appId: string, data: CreateItemRequest) => {
    set({ itemsLoading: true, itemsError: null });
    try {
      const item = await itemsApi.createItem(appId, data);
      set((state) => ({
        items: [...state.items, item],
        currentItem: item,
        itemsLoading: false,
      }));
      return item;
    } catch (error) {
      set({
        itemsLoading: false,
        itemsError: error instanceof Error ? error.message : 'Failed to create item',
      });
      throw error;
    }
  },

  updateItem: async (appId: string, itemId: string, data: UpdateItemRequest) => {
    set({ itemsLoading: true, itemsError: null });
    try {
      const updated = await itemsApi.updateItem(appId, itemId, data);
      set((state) => ({
        items: state.items.map((i) => (i.itemId === itemId ? updated : i)),
        currentItem: updated,
        itemsLoading: false,
      }));
      return updated;
    } catch (error) {
      set({
        itemsLoading: false,
        itemsError: error instanceof Error ? error.message : 'Failed to update item',
      });
      throw error;
    }
  },

  clearCurrentItem: () => set({ currentItem: null }),

  clearItemsError: () => set({ itemsError: null }),
});
